import pathfinder from '@fiverr-private/pathfinder';
import BaseAPI from '../base';
import { API_CONTEXT, ApiEndpoint } from './constants';

class MessagesAPI extends BaseAPI {
    constructor() {
        super(API_CONTEXT);
    }

    /**
     * Create a new message
     *
     * @param {Object} config
     * @param {number} config.senderId - The user sending the message
     * @param {string} config.recipientUsername - The user which the message is intended for
     * @param {string} config.relatedGigId - The related gig id.
     * @param {string} config.professionRelatedTranslationKey - The profession related translation key.
     * @param {Array<Object>} [config.pendingAttachmentIds] - The message id to download the attachments for
     * @param {string} config.body - The message body.
     * @returns {Promise<Object>}
     */
    async create({
        recipientUsername,
        relatedGigId,
        professionRelatedTranslationKey,
        pendingAttachmentIds = [],
        body
    }) {
        const url = pathfinder('message_create');

        const payload = {
            participants_usernames: [recipientUsername],
            content_type: 'text',
            content_blocks: constructContentBlock({
                relatedGigId,
                body,
                professionRelatedTranslationKey
            }),
            pending_attachment_ids: pendingAttachmentIds
        };

        return super.post({
            url,
            payload,
            apiName: ApiEndpoint.CREATE
        });
    }

    /**
     * Create a new request to order message
     *
     * @param {Object} config
     * @param {string} config.username - The user which the message is intended for
     * @param {string} config.relatedGigId - The related gig id
     * @param {string} config.relatedGigPackageType - The related gig package type
     * @param {Array<Object>} [config.attachmentsIds] - The message id to download the attachments for
     * @param {string} config.message - The message body.
     * @returns {Promise<Object>}
     */
    async createRequestToOrder({
        username,
        relatedGigId,
        relatedGigPackageType,
        attachmentsIds = [],
        message
    }) {
        const url = pathfinder('create_request_to_order');

        const payload = {
            recipient_username: username,
            related_gig_id: relatedGigId,
            gig_package_type: relatedGigPackageType,
            attachments: attachmentsIds,
            message_body: message
        };

        return super.put({
            url,
            payload,
            apiName: ApiEndpoint.CREATE_REQUEST_TO_ORDER
        });
    }
}

const constructContentBlock = ({
    relatedGigId,
    body,
    professionRelatedTranslationKey
}) => {
    if (relatedGigId) {
        return [
            {
                type: 'custom',
                custom_type: 'gig_related',
                related_object_id: relatedGigId
            },
            { type: 'text', plain_text: body }
        ];
    }

    if (professionRelatedTranslationKey) {
        return [
            {
                type: 'custom',
                custom_type: 'profession_related',
                related_object_id: professionRelatedTranslationKey,
                plain_text: body
            }
        ];
    }

    return [{ type: 'text', plain_text: body }];
};

export default new MessagesAPI();
